<template>
    <v-container>

        <v-row>
            <v-col cols="12" class="title-section">
                {{ $t('routines.almost_done') }}
            </v-col>
            <v-col cols="12">
                <kimsa-text-field
                    bolder-label
                    :label="$t('routines.duration')"
                    :placeholder="$t('common.write_here')"
                    type="number"
                    input-mode="numeric"
                    :value="duration"
                    :suffix="$t('common.short_minutes')"
                    @change="duration = $event"
                    outlined
                    centered-value
                />
            </v-col>
            <v-col cols="12" class="p-relative">
                <kimsa-slider-vertical
                    color="secondary"
                    :labels-interval="0"
                    :value="effort" :step="1"
                    :min="0" :max="10"
                    with-background
                    @change="effort = $event"
                    ref="effort"
                />
                <div class="effort-label">{{ effortLabel }}</div>
            </v-col>
            <v-col cols="12">
                <kimsa-text-field
                    bolder-label
                    :label="$t('common.comment')"
                    :placeholder="$t('common.write_here')"
                    :value="comment"
                    @change="comment = $event"
                    outlined
                />
            </v-col>
            <v-col cols="12">
                <v-btn
                    class="normal-btn black--text" @click="finish"
                    block color="primary" depressed large
                    :loading="loading" :disabled="!duration"
                >
                    {{ $t('routines.finish_training') }}
                </v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>

import {db, FieldValue, Timestamp} from "@/utils/Firebase";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaSliderVertical from "@/components/common/inputs/KimsaSliderVertical";
import {mapActions, mapState} from "vuex";

export default {
    name: "PostAssignedRoutine",
    components: {KimsaSliderVertical, KimsaTextField},
    props: {
        assignedRoutineId: {
            type: String,
            default: '',
        },
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            loadingContent: false,
            loading: false,
            routine: null,
            duration: null,
            effort: '5',
            comment: '',
            wellness: null,
            currentLastWellness: null,
        }
    },
    mounted() {
        const vm = this
        vm.loadWellness()
    },
    computed: {
        ...mapState(['currentRoutine']),
        effortLabel(vm = this) {
            switch (vm.effort) {
                case '0':
                    return vm.$t('routines.minimum')
                case '1':
                case '2':
                    return vm.$t('routines.lightweight')
                case '3':
                case '4':
                    return vm.$t('routines.moderate')
                case '5':
                case '6':
                    return vm.$t('routines.hard')
                case '7':
                case '8':
                    return vm.$t('routines.very_hard')
                case '9':
                    return vm.$t('routines.almost_maximum')
                case '10':
                    return vm.$t('routines.maximum')
            }
        },
        studentId(vm = this) {
            return vm.currentUser?.uid || null
        },
        charge(vm = this) {
            return Number(vm.duration || 0) * Number(vm.effort || 0)
        }
    },
    methods: {
        ...mapActions(['bindUserAccount']),
        async finish() {
            const vm = this
            vm.loading = true
            await db.collection('assignedRoutines').doc(vm.assignedRoutineId).update({
                'advance.post': true,
                'advance.postInfo': {
                    duration: vm.duration,
                    effort: vm.effort,
                    comment: vm.comment
                }
            })

            await vm.updateCounters()
            await vm.sendMessageToCoach()

            vm.loading = false
            vm.$emit('finish')
        },
        async sendMessageToCoach() {
            const vm = this

            let message = `${vm.$t('common.date')}: ${vm.$moment().format('DD-MM-YYYY')} \n${vm.$t('common.wellness')}: ${vm.wellness / 4} ${vm.getWellness()} \n${vm.$t('routines.duration')}: ${vm.duration}${vm.$t('common.short_minutes')} \n${vm.$t('exercises.effort')}: ${vm.effort} \n${vm.$t('exercises.charge')}: ${vm.charge}`
            if (vm.comment) message += `\n${vm.$t('common.comment')}: ${vm.comment}`

            let chatRef = await db.collection('privateChats')
                .where('type', '==', 'coach_student')
                .where('participants', 'in', [[vm.coachId, vm.studentId]])
                .get()

            let chatId = null
            if (chatRef.docs.length > 0) {
                chatId = chatRef.docs[0].id
            } else {
                let newChat = await db.collection('privateChats').add({
                    type: 'coach_student',
                    participants: [vm.coachId, vm.studentId]
                })
                chatId = newChat.id
            }

            let payload = {
                message: message,
                from: vm.studentId,
                read: false,
                timestamp: Timestamp.now(),
                type: 'text'
            }

            await db.collection('privateChats').doc(chatId).collection('messages').add(payload)
            await db.collection('privateChats').doc(chatId).update({lastMessage: payload})
        },
        async updateCounters() {
            const vm = this

            let lastWellness = vm.account?.lastWellness || []
            lastWellness.push(vm.wellness)
            if (lastWellness.length === 13) lastWellness.shift();
            vm.currentLastWellness = lastWellness

            await db.collection('users').doc(vm.studentId).update({
                'counter.week': FieldValue.increment(1),
                'counter.month': FieldValue.increment(1),
                'counter.durationWeek': FieldValue.increment(vm.duration),
                'counter.durationMonth': FieldValue.increment(vm.duration),
                lastWellness: lastWellness
            })
            await vm.bindUserAccount(vm.currentUser)
        },
        loadWellness() {
            const vm = this
            if (!vm.currentRoutine?.advance?.preInfo) return;
            let preInfo = vm.currentRoutine.advance.preInfo
            vm.wellness = preInfo.muscles_hurt + preInfo.sleep_night + preInfo.stressed + preInfo.tired
        },
        getWellness() {
            const vm = this
            let wellnessArray = vm.fillWellness(vm.currentLastWellness)
            return wellnessArray[wellnessArray.length -1]
        },
        fillWellness(wellness) {
            const vm = this
            let array = []

            const lastSixValues = []
            for (let i = 5; i < wellness.length; i++) lastSixValues.push(wellness.slice(i - 5, i + 1))

            for (let i = 0; i < lastSixValues.length; i++) {
                const subArray = lastSixValues[i]
                const avg = Number(subArray.reduce((a, b) => a + b) / subArray.length).toFixed(2)
                const desvest = vm.calcDesvest(subArray)
                const currentValue = wellness[i + 5]
                array.push(Number((currentValue - avg) / desvest).toFixed(2))
            }

            let newArray = new Array(7).fill('&#9898;')
            newArray.splice(0, array.length, ...array.map(item => vm.getColorWellness(Number(item))));
            return newArray
        },
        calcDesvest(array) {
            const avg = array.reduce((a, b) => a + b) / array.length
            const desvests = array.map(x => Math.pow(x - avg, 2))
            if (desvests.every(element => element === 0)) return 1
            const sumDesvests = desvests.reduce((a, b) => a + b)
            return Math.sqrt(sumDesvests / array.length).toFixed(2)
        },
        getColorWellness(wellness) {
            if (wellness <= -1.65) return '&#128308;' // red
            else if (wellness < 1.65) return '&#9898;' // grey
            else return '&#128994;' // green
        },
    },
    watch: {
        currentRoutine() {
            if (!this.wellness) this.loadWellness()
        }
    }
}
</script>

<style scoped>

.title-section {
    font-size: 30px;
    font-weight: bold;
    color: #333333;
}

.effort-label {
    font-size: 17px;
    font-weight: bold;
    color: #707070;
    position: absolute;
    left: 10%;
    top: 45%;
}

</style>